import {isLoggedIn} from '@github-ui/client-env'
import {getQuery} from '@github-ui/list-view-items-issues-prs/Query'
import {getDefaultValues} from '@github-ui/relay-query-cache/helpers'
import {repoSidebarLinks} from '@github-ui/repos-issues-sidebar-ui/repos-issues-sidebar-links'
import {useCallback} from 'react'
import type {Environment} from 'react-relay'
import {fetchQuery, useRelayEnvironment} from 'react-relay'

import type {IssueIndexPageQuery} from '../pages/__generated__/IssueIndexPageQuery.graphql'
import IssueIndexPageQueryNode from '../pages/__generated__/IssueIndexPageQuery.graphql'
import {getIssueSearchTypeFromEnvironment} from '../utils/issue-search-type'

export function buildIssueIndexVariables(
  rawQuery: string,
  repo: {owner: string; name: string},
  environment?: Environment,
): Record<string, unknown> {
  const bestMatchSortEnabled =
    (environment?.options as {bestMatchSortEnabled?: boolean} | undefined)?.bestMatchSortEnabled ?? false

  const variables: Record<string, unknown> = {
    query: getQuery(rawQuery, repo, bestMatchSortEnabled),
    owner: repo.owner,
    name: repo.name,
    skip: 0,
    showIssueFieldPills: true,
  }

  const issueSearchType = getIssueSearchTypeFromEnvironment(environment)
  if (issueSearchType) {
    variables.type = issueSearchType
  }

  return variables
}

export function usePreheatSidebarQueries(owner: string | undefined, repo: string | undefined) {
  const environment = useRelayEnvironment()

  const preheatAll = useCallback(() => {
    if (!isLoggedIn()) return
    if (!owner || !repo) return

    for (const link of repoSidebarLinks) {
      const variables = buildIssueIndexVariables(link.query, {owner, name: repo}, environment)
      fetchQuery<IssueIndexPageQuery>(
        environment,
        IssueIndexPageQueryNode,
        variables as IssueIndexPageQuery['variables'],
        {
          networkCacheConfig: {
            metadata: {
              ttl: 1000 * 60 * 60, // 1 hour
              defaultValues: getDefaultValues(IssueIndexPageQueryNode),
              preheatSource: 'sidebar-idle',
            },
          },
        },
      ).subscribe({})
    }
  }, [environment, owner, repo])

  return {preheatAll}
}
